/**
 * Exit Intent Popup Component
 *
 * Shows a modal when the user attempts to leave the page.
 * Captures email leads with a special offer.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { supabase } from '../../services/supabaseService';
import { trackExitIntentPopup, getStoredUTMParams } from '../../utils/analytics';

interface ExitIntentPopupProps {
  /** Delay in ms before enabling exit intent detection */
  delay?: number;
  /** Storage key to track if popup was already shown */
  storageKey?: string;
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
  delay = 5000,
  storageKey = 'clinafy_exit_popup_shown',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);

  // Check if popup was already shown in this session
  const wasAlreadyShown = useCallback(() => {
    try {
      return sessionStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  }, [storageKey]);

  // Mark popup as shown
  const markAsShown = useCallback(() => {
    try {
      sessionStorage.setItem(storageKey, 'true');
    } catch {
      // Ignore storage errors
    }
  }, [storageKey]);

  // Handle mouse leave (exit intent)
  const handleMouseLeave = useCallback((e: MouseEvent) => {
    // Only trigger when mouse moves to top of viewport (likely leaving)
    if (e.clientY <= 0 && !wasAlreadyShown() && isEnabled) {
      setIsOpen(true);
      markAsShown();
      trackExitIntentPopup('shown');
    }
  }, [wasAlreadyShown, markAsShown, isEnabled]);

  // Enable exit intent detection after delay
  useEffect(() => {
    // Don't enable on mobile (no reliable exit intent)
    if (window.innerWidth < 768) return;

    // Don't enable if already shown
    if (wasAlreadyShown()) return;

    const timer = setTimeout(() => {
      setIsEnabled(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, wasAlreadyShown]);

  // Add/remove event listener
  useEffect(() => {
    if (!isEnabled) return;

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [isEnabled, handleMouseLeave]);

  const handleClose = () => {
    setIsOpen(false);
    trackExitIntentPopup('dismissed');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get UTM params for attribution
      const utmParams = getStoredUTMParams();

      // Save to leads table
      const { error: insertError } = await supabase
        .from('leads')
        .insert({
          email: email.toLowerCase().trim(),
          source: 'exit_intent',
          status: 'new',
          message: 'Desconto de 20% no primeiro mês - Exit Intent Popup',
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          referrer_url: utmParams.referrer || null,
        });

      if (insertError) {
        // Check if duplicate
        if (insertError.code === '23505') {
          setSuccess(true);
          trackExitIntentPopup('converted', email);
        } else {
          throw insertError;
        }
      } else {
        setSuccess(true);
        trackExitIntentPopup('converted', email);
      }
    } catch (err) {
      console.error('Error saving exit intent lead:', err);
      setError('Erro ao salvar. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: 'rgba(0,0,0,0.05)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Header with gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1B6A63 0%, #27AB83 100%)',
            color: 'white',
            py: 4,
            px: 3,
            textAlign: 'center',
          }}
        >
          <LocalOfferIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Espera! Não vá ainda...
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Ganhe 20% de desconto no primeiro mês
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {success ? (
            <Box>
              <Typography variant="h5" color="success.main" fontWeight="bold" gutterBottom>
                Cupom enviado para seu email!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Use o código <strong>BEMVINDO20</strong> no checkout para aplicar seu desconto.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                href="/signup?trial=true"
                sx={{ mt: 3 }}
              >
                Começar Agora
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Deixe seu email e receba um cupom exclusivo de <strong>20% OFF</strong> válido por 48 horas.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  type="email"
                  label="Seu melhor email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@email.com"
                  disabled={isSubmitting}
                  sx={{ mb: 2 }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #1B6A63 0%, #27AB83 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #155a54 0%, #1f8a69 100%)',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Quero meu desconto'
                  )}
                </Button>
              </Box>

              <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                Não enviamos spam. Você pode cancelar a qualquer momento.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
